import { WidgetType, type EditorView } from '@codemirror/view';
import { parseNote } from '@rbv/shared';
import { vault } from '../sync/vault.js';
import { vaultFileUrl } from '../navigation.js';
import { extractSection, type MediaKind, type ParsedTable } from './helpers.js';
import { buildTableDom, renderMarkdownInto } from './minirender.js';
import type { EditorCtx, NavTarget } from './context.js';

/** Follow-on-tap wiring: preventDefault only on the widget itself so the
 * editor still handles taps elsewhere (iOS). */
function wireFollow(elm: HTMLElement, follow: () => void): void {
  elm.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    e.stopPropagation();
  });
  elm.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    follow();
  });
}

// ---------------------------------------------------------------------------

export class LinkWidget extends WidgetType {
  constructor(
    readonly display: string,
    readonly unresolved: boolean,
    readonly nav: NavTarget,
    readonly ctx: EditorCtx,
    readonly uploading = false,
  ) {
    super();
  }

  override eq(other: LinkWidget): boolean {
    return (
      other.display === this.display &&
      other.unresolved === this.unresolved &&
      other.uploading === this.uploading &&
      other.nav.target === this.nav.target &&
      other.nav.heading === this.nav.heading &&
      other.nav.blockId === this.nav.blockId
    );
  }

  toDOM(): HTMLElement {
    const s = document.createElement('span');
    s.className = this.uploading
      ? 'lp-link lp-uploading'
      : this.unresolved
        ? 'lp-link lp-link-unresolved'
        : 'lp-link';
    s.textContent = this.uploading ? 'Uploading…' : this.display;
    s.setAttribute('role', 'link');
    if (!this.uploading) wireFollow(s, () => this.ctx.navigate(this.nav));
    return s;
  }
}

// ---------------------------------------------------------------------------

export class MediaWidget extends WidgetType {
  constructor(
    readonly url: string,
    readonly kind: Exclude<MediaKind, null>,
    readonly alt: string,
  ) {
    super();
  }

  override eq(other: MediaWidget): boolean {
    return other.url === this.url && other.kind === this.kind && other.alt === this.alt;
  }

  toDOM(): HTMLElement {
    const wrap = document.createElement('span');
    wrap.className = `lp-media lp-media-${this.kind}`;
    if (this.kind === 'image') {
      const img = document.createElement('img');
      img.src = this.url;
      img.alt = this.alt;
      img.loading = 'lazy';
      wrap.appendChild(img);
    } else if (this.kind === 'audio') {
      const a = document.createElement('audio');
      a.src = this.url;
      a.controls = true;
      a.preload = 'metadata';
      wrap.appendChild(a);
    } else {
      const v = document.createElement('video');
      v.src = this.url;
      v.controls = true;
      v.playsInline = true;
      v.preload = 'metadata';
      wrap.appendChild(v);
    }
    return wrap;
  }

  override ignoreEvent(): boolean {
    return true;
  }
}

// ---------------------------------------------------------------------------

export class NoteEmbedWidget extends WidgetType {
  constructor(
    readonly targetId: string,
    readonly title: string,
    readonly content: string,
    readonly nav: NavTarget,
    readonly ctx: EditorCtx,
  ) {
    super();
  }

  override eq(other: NoteEmbedWidget): boolean {
    return (
      other.targetId === this.targetId &&
      other.title === this.title &&
      other.content === this.content &&
      other.nav.heading === this.nav.heading &&
      other.nav.blockId === this.nav.blockId
    );
  }

  toDOM(): HTMLElement {
    const box = document.createElement('div');
    box.className = 'lp-embed';
    const head = document.createElement('div');
    head.className = 'lp-embed-title';
    head.textContent = this.title;
    wireFollow(head, () => this.ctx.navigate(this.nav));
    box.appendChild(head);
    const body = document.createElement('div');
    body.className = 'lp-embed-body';
    if (this.content) {
      renderMarkdownInto(body, this.content, this.ctx);
    } else {
      const empty = document.createElement('span');
      empty.className = 'lp-embed-empty';
      empty.textContent = 'Section not found';
      body.appendChild(empty);
    }
    box.appendChild(body);
    return box;
  }
}

/** Extract the transcluded section of a note for ![[note#h]] / ![[note#^b]]. */
export function embedContent(targetId: string, heading?: string, blockId?: string): string {
  const md = vault.noteContent(targetId);
  const parse = parseNote(md);
  return extractSection(md, { heading, blockId }, parse);
}

// ---------------------------------------------------------------------------

export class TableWidget extends WidgetType {
  constructor(
    readonly source: string,
    readonly parsed: ParsedTable,
    readonly ctx: EditorCtx,
  ) {
    super();
  }

  override eq(other: TableWidget): boolean {
    return other.source === this.source;
  }

  toDOM(view: EditorView): HTMLElement {
    const dom = buildTableDom(this.parsed, this.ctx);
    dom.classList.add('lp-table-widget');
    // Tapping the rendered table places the cursor inside it (raw source).
    dom.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('.lp-link, .lp-tag')) return;
      const pos = view.posAtDOM(dom);
      view.dispatch({ selection: { anchor: pos } });
      view.focus();
    });
    return dom;
  }
}

// ---------------------------------------------------------------------------

export class CheckboxWidget extends WidgetType {
  constructor(readonly checked: boolean) {
    super();
  }

  override eq(other: CheckboxWidget): boolean {
    return other.checked === this.checked;
  }

  toDOM(view: EditorView): HTMLElement {
    const label = document.createElement('span');
    label.className = 'lp-task';
    const box = document.createElement('input');
    box.type = 'checkbox';
    box.checked = this.checked;
    box.className = 'lp-task-box';
    label.appendChild(box);
    label.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      e.stopPropagation();
    });
    label.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      // Widget replaces "[x] " starting at the marker: state char is pos+1.
      const pos = view.posAtDOM(label);
      const at = pos + 1;
      if (at >= view.state.doc.length) return;
      const cur = view.state.doc.sliceString(at, at + 1);
      const next = cur === ' ' ? 'x' : ' ';
      view.dispatch({ changes: { from: at, to: at + 1, insert: next } });
    });
    return label;
  }

  override ignoreEvent(): boolean {
    return true;
  }
}

// ---------------------------------------------------------------------------

export class HrWidget extends WidgetType {
  override eq(): boolean {
    return true;
  }

  toDOM(): HTMLElement {
    const s = document.createElement('span');
    s.className = 'lp-hr-wrap';
    const hr = document.createElement('hr');
    hr.className = 'lp-hr';
    s.appendChild(hr);
    return s;
  }
}

export class CodeLangWidget extends WidgetType {
  constructor(readonly lang: string) {
    super();
  }

  override eq(other: CodeLangWidget): boolean {
    return other.lang === this.lang;
  }

  toDOM(): HTMLElement {
    const s = document.createElement('span');
    s.className = 'lp-code-lang';
    s.textContent = this.lang;
    return s;
  }
}

export class BulletWidget extends WidgetType {
  override eq(): boolean {
    return true;
  }

  toDOM(): HTMLElement {
    const s = document.createElement('span');
    s.className = 'lp-bullet';
    s.textContent = '•';
    return s;
  }
}

/** Resolve an embed URL for media widgets: vault path when resolved, direct
 * URL when external. */
export function mediaUrlFor(path: string): string {
  return vaultFileUrl(path);
}
